import type { TradingAccount } from "@/lib/accounts/types";
import { useAccountsStore } from "@/lib/accounts/store";
import { clearStoredBrokers } from "@/lib/brokers/storage";
import { clearStoredChartSettings } from "@/lib/chart-settings/storage";
import { clearStoredChartIndicators } from "@/lib/chart-indicators/storage";
import { clearStoredEconomicCalendar } from "@/lib/economic-calendar/storage";
import { clearStoredQuotesWatchlist } from "@/lib/quotes-watchlist/storage";
import { clearStoredChartObjectsSettings } from "@/lib/chart-drawings/storage";
import { clearStoredSettings } from "@/lib/settings/storage";
import { formatTradingAccountKey } from "@/vybe-trader-client/chart/utils/accountHelpers";
import {
  clearMailboxMessages,
  clearAllMailboxMessages,
} from "@/vybe-trader-client/mailbox/storage";
import { marketDataDb } from "@/lib/market-data-db";
import {
  clearIdbAccountMarketData,
  clearIdbSymbolMarketData,
  clearAllIdbMarketData,
} from "./idb-ops";
import type { AccountCacheTypeId, AppCacheTypeId } from "./inventory";
import { invalidateStorageInventorySnapshot } from "./inventory-snapshot";

function resolveAccount(accountId: string): TradingAccount | null {
  const { accounts } = useAccountsStore.getState();
  return accounts.find((a) => a.id === accountId) ?? null;
}

function marketDataAccountKey(account: TradingAccount): string {
  return `${account.companyName}_${account.serverName}`;
}

/**
 * Clear all cached data for a trading account (market data + mailbox + memory).
 */
export async function clearAccountCache(accountId: string): Promise<boolean> {
  const account = resolveAccount(accountId);
  if (!account) return false;

  try {
    await clearIdbAccountMarketData(
      account.companyName,
      account.serverName,
    );
    await marketDataDb.invalidateAccountCache(
      marketDataAccountKey(account),
    );
    const mailboxKey = formatTradingAccountKey(account);
    if (mailboxKey) clearMailboxMessages(mailboxKey);
    invalidateStorageInventorySnapshot();
    return true;
  } catch (error) {
    console.error("Error clearing account cache:", error);
    return false;
  }
}

/**
 * Clear one cache type for an account.
 */
export async function clearAccountCacheType(
  accountId: string,
  type: AccountCacheTypeId,
): Promise<boolean> {
  const account = resolveAccount(accountId);
  if (!account) return false;

  try {
    if (type === "marketData") {
      await clearIdbAccountMarketData(
        account.companyName,
        account.serverName,
      );
      await marketDataDb.invalidateAccountCache(
        marketDataAccountKey(account),
      );
    } else if (type === "mailbox") {
      const mailboxKey = formatTradingAccountKey(account);
      if (mailboxKey) clearMailboxMessages(mailboxKey);
    }
    invalidateStorageInventorySnapshot();
    return true;
  } catch (error) {
    console.error("Error clearing account cache type:", error);
    return false;
  }
}

/**
 * Clear market data for one symbol on an account.
 */
export async function clearAccountSymbolCache(
  accountId: string,
  symbol: string,
): Promise<boolean> {
  const account = resolveAccount(accountId);
  if (!account || !symbol) return false;

  try {
    await clearIdbSymbolMarketData(
      account.companyName,
      account.serverName,
      symbol,
    );
    await marketDataDb.invalidateSymbolCache(
      marketDataAccountKey(account),
      symbol,
    );
    invalidateStorageInventorySnapshot();
    return true;
  } catch (error) {
    console.error("Error clearing symbol cache:", error);
    return false;
  }
}

/** @deprecated Use clearAccountCache */
export function clearAccountServerCache(_accountId: string): boolean {
  return true;
}

/** @deprecated Use clearAccountCache */
export async function clearAllAccountCache(
  accountId: string,
): Promise<boolean> {
  return clearAccountCache(accountId);
}

/** @deprecated Server tier removed; clears full account market data */
export async function clearServerCacheByCompanyAndServer(
  company: string,
  serverName: string,
): Promise<boolean> {
  try {
    await clearIdbAccountMarketData(company, serverName);
    await marketDataDb.invalidateAccountCache(`${company}_${serverName}`);
    invalidateStorageInventorySnapshot();
    return true;
  } catch {
    return false;
  }
}

const APP_CLEAR_HANDLERS: Record<AppCacheTypeId, () => boolean> = {
  brokers: () => {
    clearStoredBrokers();
    return true;
  },
  chartSettings: clearStoredChartSettings,
  chartIndicators: clearStoredChartIndicators,
  chartDrawings: clearStoredChartObjectsSettings,
  economicCalendar: clearStoredEconomicCalendar,
  quotesWatchlist: clearStoredQuotesWatchlist,
  settings: clearStoredSettings,
};

/**
 * Clear app cache (all modules or selected types).
 */
export function cleanAppCache(types?: AppCacheTypeId[]): boolean {
  if (typeof window === "undefined") return false;

  try {
    const keys = types ?? (Object.keys(APP_CLEAR_HANDLERS) as AppCacheTypeId[]);
    for (const key of keys) {
      APP_CLEAR_HANDLERS[key]();
    }
    invalidateStorageInventorySnapshot();
    return true;
  } catch (error) {
    console.error("Error clearing app cache:", error);
    return false;
  }
}

/**
 * Clear all caches (app + accounts + IDB + mailbox) and reload.
 */
export async function cleanAllCaches(): Promise<boolean> {
  if (typeof window === "undefined") return false;

  try {
    cleanAppCache();
    const { accounts } = useAccountsStore.getState();
    for (const account of accounts) {
      await clearAccountCache(account.id);
    }
    await clearAllIdbMarketData();
    clearAllMailboxMessages();
    window.location.reload();
    return true;
  } catch (error) {
    console.error("Error clearing all caches:", error);
    return false;
  }
}

/**
 * Check and clean old account caches (TTL) — not implemented in v1.
 */
export function checkAndCleanOldAccountCaches(): void {
  if (typeof window === "undefined") return;
}
